// AI Assistant — super_admin only (John & Priya). A read-only chat that
// answers questions about orders, payables, vendors and cockpit numbers.
// Every answer is built from the same queries the screens use; the model
// can read everything and change nothing.

import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { aiChatApi } from '../api/services';
import InfoTip from '../components/InfoTip';
import toast from 'react-hot-toast';
import { Loader2, Send, Sparkles, Plus, Wrench, Eye, X, History, FileDown } from 'lucide-react';
import { format } from 'date-fns';

const SUGGESTIONS = [
  'What do we owe each vendor this month, and is the total trustworthy?',
  'Which orders have been stuck the longest, and with whom?',
  'How much did reships cost us this month, and why did they happen?',
  'How is each vendor performing this month?',
];

// Grouped sample questions for the "How to use" panel — clicking one sends it.
const HELP_SECTIONS = [
  {
    heading: 'Before paying a vendor',
    samples: [
      'What do we owe FRT for May, split into regular orders and reships?',
      'Are there any unpriced lines making this month’s payable understated?',
      'How much did reship discounts save us this month?',
      'When did the price of Cenforce 100 change for FRT?',
    ],
  },
  {
    heading: 'Chasing a specific order',
    samples: [
      'What happened to order IDM260522012W? Give me the full story.',
      'Why is this order still not delivered?',
      'Who has been holding this order the longest and why?',
    ],
  },
  {
    heading: 'Questioning the team',
    samples: [
      'Which orders have sat with Dispatch Admin for more than a week?',
      'List the orders stuck in transit over 14 days with their tracking numbers.',
      'How many out-of-stock responses did each vendor give this month?',
      'What exceptions did Kalpana handle this week?',
    ],
  },
];

// Order IDs in answers (IDM…) become links to the order page.
function linkify(text) {
  const parts = String(text).split(/(IDM[A-Z0-9]{6,})/g);
  return parts.map((p, i) =>
    /^IDM[A-Z0-9]{6,}$/.test(p)
      ? <Link key={i} to={`/orders/by-external/${p}`} className="font-mono text-blue-600 hover:underline">{p}</Link>
      : <span key={i}>{p}</span>
  );
}

// Inline markdown: **bold** (plus order-ID links inside and outside bold).
function renderInline(text) {
  return String(text).split(/(\*\*[^*]+\*\*)/g).map((p, i) =>
    /^\*\*[^*]+\*\*$/.test(p)
      ? <strong key={i} className="font-semibold">{linkify(p.slice(2, -2))}</strong>
      : <span key={i}>{linkify(p)}</span>
  );
}

// Block markdown: paragraphs + "- " / "1. " bullets + "### " headings.
// The model is told to keep to bold and bullets, but render the rest
// gracefully anyway.
function MarkdownLite({ text }) {
  const lines = String(text).split('\n');
  const blocks = [];
  let list = null;
  for (const line of lines) {
    const bullet = line.match(/^\s*(?:[-*]|\d+[.)])\s+(.*)/);
    const heading = line.match(/^#{1,4}\s+(.*)/);
    if (bullet) {
      if (!list) { list = []; blocks.push({ type: 'ul', items: list }); }
      list.push(bullet[1]);
    } else {
      list = null;
      if (heading) blocks.push({ type: 'h', text: heading[1] });
      else if (line.trim() !== '') blocks.push({ type: 'p', text: line });
    }
  }
  return (
    <div className="space-y-1.5">
      {blocks.map((b, i) => {
        if (b.type === 'ul') {
          return (
            <ul key={i} className="list-disc pl-4 space-y-0.5">
              {b.items.map((it, j) => <li key={j}>{renderInline(it)}</li>)}
            </ul>
          );
        }
        if (b.type === 'h') return <div key={i} className="font-semibold">{renderInline(b.text)}</div>;
        return <div key={i}>{renderInline(b.text)}</div>;
      })}
    </div>
  );
}

// Client-side CSV download of a tool result the assistant used. BOM so
// Excel opens it with correct encoding (₹, names with accents).
function downloadRowsCsv(label, rows) {
  const keys = [...new Set(rows.flatMap((r) => Object.keys(r)))];
  const esc = (v) => {
    if (v == null) return '';
    const s = typeof v === 'object' ? JSON.stringify(v) : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [keys.join(',')]
    .concat(rows.map((r) => keys.map((k) => esc(r[k])).join(',')))
    .join('\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `AI-${label.replace(/\s+/g, '-')}-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

function Bubble({ role, children, meta }) {
  const mine = role === 'user';
  return (
    <div className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
        mine ? 'bg-blue-600 text-white rounded-br-sm whitespace-pre-wrap' : 'bg-white border border-gray-200 text-gray-900 rounded-bl-sm'
      }`}>
        {children}
        {meta && <div className="mt-1.5 text-[10px] text-gray-400">{meta}</div>}
      </div>
    </div>
  );
}

export default function AiChat() {
  const [messages, setMessages] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [usage, setUsage] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [conversations, setConversations] = useState([]);
  const bottomRef = useRef(null);

  function mapMessages(rows) {
    return rows.map((m) => ({
      role: m.role, content: m.content,
      toolsUsed: m.tool_payload?.tools_used || [],
      exports: m.tool_payload?.exports || [],
      costUsd: m.cost_usd,
    }));
  }

  function refreshConversations() {
    aiChatApi.conversations().then((r) => setConversations(r.data.conversations || [])).catch(() => {});
  }

  useEffect(() => {
    aiChatApi.usage().then((r) => setUsage(r.data)).catch(() => {});
    refreshConversations();
    aiChatApi.latest().then((r) => {
      if (r.data.conversationId) {
        setConversationId(r.data.conversationId);
        setMessages(mapMessages(r.data.messages));
      }
    }).catch(() => {});
  }, []);

  function openConversation(id) {
    if (id === conversationId) return;
    aiChatApi.conversation(id).then((r) => {
      setConversationId(r.data.conversationId);
      setMessages(mapMessages(r.data.messages));
    }).catch(() => toast.error('Could not load that conversation'));
  }

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sending]);

  async function send(text) {
    const message = (text ?? input).trim();
    if (!message || sending) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: message }]);
    setSending(true);
    try {
      const res = await aiChatApi.send({ message, conversationId });
      setConversationId(res.data.conversationId);
      setMessages((m) => [...m, {
        role: 'assistant',
        content: res.data.reply,
        toolsUsed: res.data.toolsUsed || [],
        exports: res.data.exports || [],
        costUsd: res.data.costUsd,
      }]);
      setUsage((u) => u ? { ...u, spentThisMonthUsd: res.data.spentThisMonthUsd } : u);
      refreshConversations();
    } catch (err) {
      const msg = err.response?.data?.error || 'The assistant could not answer — try again.';
      toast.error(msg);
      setMessages((m) => [...m, { role: 'assistant', content: `⚠️ ${msg}`, toolsUsed: [] }]);
    } finally { setSending(false); }
  }

  function newChat() {
    setConversationId(null);
    setMessages([]);
  }

  const disabled = usage && !usage.enabled;
  const overCap = usage && usage.spentThisMonthUsd >= usage.capUsd;

  return (
    <div className="p-6 max-w-6xl mx-auto h-[calc(100vh-1rem)] flex flex-col">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-violet-600" />
          <h1 className="text-2xl font-semibold text-gray-900">AI Assistant</h1>
          <InfoTip title="What this can and can't do" align="left">
            Ask anything about orders, vendor payables, pricing, or team
            queues — answers come from the live database through the same
            calculations the pages use. It is <b>read-only</b>: it cannot
            cancel, hold, pay or edit anything. Every question and answer is
            logged, and spending is capped per month.
          </InfoTip>
        </div>
        <div className="flex items-center gap-3">
          {usage && (
            <span className={`text-xs ${overCap ? 'text-rose-600 font-medium' : 'text-gray-500'}`}>
              ${usage.spentThisMonthUsd.toFixed(2)} / ${usage.capUsd} this month
            </span>
          )}
          <button onClick={() => setShowHistory((v) => !v)}
                  title="Your past conversations"
                  className={`flex items-center gap-1 px-3 py-1.5 border rounded-lg text-sm ${
                    showHistory ? 'border-blue-400 bg-blue-50 text-blue-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}>
            <History className="w-4 h-4" /> History
          </button>
          <button onClick={() => setShowHelp((v) => !v)}
                  title="How to use this assistant"
                  className={`flex items-center gap-1 px-3 py-1.5 border rounded-lg text-sm ${
                    showHelp ? 'border-violet-400 bg-violet-50 text-violet-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}>
            <Eye className="w-4 h-4" /> How to use
          </button>
          <button onClick={newChat}
                  className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50">
            <Plus className="w-4 h-4" /> New chat
          </button>
        </div>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Ask in plain English. It looks things up in the live system and shows where it looked.
      </p>

      {showHelp && (
        <div className="mb-4 rounded-xl border border-violet-200 bg-violet-50/50 p-4 text-sm relative">
          <button onClick={() => setShowHelp(false)}
                  className="absolute top-3 right-3 text-gray-400 hover:text-gray-600">
            <X className="w-4 h-4" />
          </button>
          <div className="font-semibold text-gray-900 mb-1">What this assistant does</div>
          <p className="text-xs text-gray-600 mb-2">
            It answers questions by looking up the <b>live system</b> — orders, payables,
            pricing, tracking, team queues — using the same calculations the pages use.
            It is <b>read-only</b>: it cannot cancel, hold, pay or edit anything, and it
            will say so if asked. Every question is logged, and order IDs in answers are
            clickable. Mention orders by their IDM number for best results.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {HELP_SECTIONS.map((s) => (
              <div key={s.heading}>
                <div className="text-[11px] uppercase tracking-wide text-violet-700 font-semibold mb-1.5">
                  {s.heading}
                </div>
                <div className="flex flex-col gap-1.5">
                  {s.samples.map((q) => (
                    <button key={q}
                            onClick={() => { setShowHelp(false); send(q); }}
                            disabled={sending || disabled || overCap}
                            className="text-left text-xs px-2.5 py-1.5 bg-white border border-gray-200 rounded-lg hover:border-violet-300 hover:bg-violet-50 text-gray-700 disabled:opacity-50">
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-gray-500 mt-3">
            Click any question to ask it. Costs about ₹0.50 per question against a $
            {usage?.capUsd ?? 10}/month budget.
          </p>
        </div>
      )}

      <div className="flex-1 flex gap-3 min-h-0">
        {showHistory && (
          <aside className="w-64 shrink-0 rounded-xl border border-gray-200 bg-white overflow-y-auto">
            <div className="px-3 py-2 text-[11px] uppercase tracking-wide text-gray-500 border-b border-gray-100 sticky top-0 bg-white">
              Your past conversations
            </div>
            {conversations.length === 0 && (
              <div className="px-3 py-6 text-xs text-gray-400 text-center">No conversations yet.</div>
            )}
            <ul className="divide-y divide-gray-100">
              {conversations.map((c) => (
                <li key={c.conversation_id}>
                  <button onClick={() => openConversation(c.conversation_id)}
                          className={`w-full text-left px-3 py-2 hover:bg-gray-50 ${
                            c.conversation_id === conversationId ? 'bg-blue-50' : ''
                          }`}>
                    <div className="text-xs text-gray-900 line-clamp-2">{c.title || '(no question)'}</div>
                    <div className="text-[10px] text-gray-400 mt-0.5">
                      {format(new Date(c.last_at), 'dd MMM yyyy')} · {c.questions} question(s)
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </aside>
        )}
        <div className="flex-1 flex flex-col min-h-0">
      <div className="flex-1 overflow-y-auto rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-3">
        {messages.length === 0 && (
          <div className="h-full flex flex-col items-center justify-center gap-3">
            <Sparkles className="w-8 h-8 text-gray-300" />
            <div className="text-sm text-gray-500">Try one of these:</div>
            <div className="flex flex-col gap-2 w-full max-w-lg">
              {SUGGESTIONS.map((s) => (
                <button key={s} onClick={() => send(s)}
                        className="text-left text-sm px-4 py-2.5 bg-white border border-gray-200 rounded-xl hover:border-blue-300 hover:bg-blue-50/50 text-gray-700">
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i}>
            <Bubble role={m.role}
                    meta={m.role === 'assistant' && m.costUsd != null ? `this answer cost $${Number(m.costUsd).toFixed(4)}` : null}>
              {m.role === 'assistant' ? <MarkdownLite text={m.content} /> : m.content}
            </Bubble>
            {m.role === 'assistant' && (m.toolsUsed?.length > 0 || m.exports?.length > 0) && (
              <div className="flex flex-wrap items-center gap-1 mt-1">
                {m.toolsUsed?.map((t, j) => (
                  <span key={j} title={t.args ? JSON.stringify(t.args) : t.error}
                        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] ${
                          t.error ? 'bg-rose-50 text-rose-600' : 'bg-gray-100 text-gray-500'
                        }`}>
                    <Wrench className="w-2.5 h-2.5" /> {t.tool.replace(/_/g, ' ')}
                  </span>
                ))}
                {m.exports?.map((ex, j) => (
                  <button key={`x${j}`}
                          onClick={() => downloadRowsCsv(ex.label, ex.rows)}
                          title="Download this data as CSV — opens in Excel"
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100">
                    <FileDown className="w-2.5 h-2.5" /> {ex.label} ({ex.rows.length}) CSV
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
        {sending && (
          <Bubble role="assistant">
            <span className="inline-flex items-center gap-2 text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" /> Looking it up…
            </span>
          </Bubble>
        )}
        <div ref={bottomRef} />
      </div>

      <form className="mt-3 flex gap-2"
            onSubmit={(e) => { e.preventDefault(); send(); }}>
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          disabled={sending || disabled || overCap}
          placeholder={
            disabled ? 'AI chat is not configured on the server.'
            : overCap ? 'Monthly AI budget reached — resets next month.'
            : 'e.g. Why is order IDM260522012W still not delivered?'
          }
          className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50"
        />
        <button type="submit" disabled={sending || !input.trim() || disabled || overCap}
                className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </form>
        </div>
      </div>
    </div>
  );
}
